import {
  NotificationPayloadSchema,
  channelName,
  type LeagueOfferStatus,
  type OfferDirection,
} from "@legacy-manager/shared";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { numero, useT, type Chave } from "../i18n";
import { useErroEmToast } from "../ui/avisos";
import {
  DetalheDeLigaSchema,
  LanceSchema,
  LiberacaoDePropostaSchema,
  PaginaDeLeiloesSchema,
  PaginaDePropostasSchema,
  PropostaCriadaSchema,
  PropostaDaLigaSchema,
  RespostaDePropostaSchema,
  VisaoDaLigaSchema,
  type ClubeDaVisao,
  type DetalheDeLiga,
  type JogadorDeOutroClube,
  type LeilaoDaLiga,
  type PropostaDaLiga,
  type VisaoDaLiga,
} from "../online/esquemas";
import { mensagemDoErro, useSessao } from "../online/sessao";
import { Crest, Money, Panel, PositionChip, Stat, StrengthBadge } from "../ui/atoms";
import { Pagina } from "../ui/Pagina";

/**
 * Mercado entre treinadores humanos: propostas, leilões e o aval do comissário.
 *
 * Duas coisas separam esta tela do mercado do modo solo, e as duas ditam o
 * desenho dela.
 *
 * A primeira é que **do outro lado tem gente**. Uma proposta não é aceita por
 * uma conta de fórmula: ela fica parada até alguém abrir o jogo e responder. Por
 * isso cada linha diz em que rodada o prazo vence e de que lado da mesa você
 * está, e por isso a contraproposta ganha explicação própria: ela encerra a
 * proposta que chegou e abre outra no sentido contrário, o que troca quem
 * espera por quem responde. Quem não percebe isso fica olhando para uma caixa
 * de recebidas vazia achando que o pedido sumiu.
 *
 * A segunda é o **comissário**. Com `commissionerApproval` ligado, o acerto
 * entre os dois treinadores não conclui nada sozinho: a proposta fica `accepted`
 * e só entra na fila da rodada depois da liberação. É a defesa da liga contra
 * combinação, e a tela precisa mostrar esse degrau em vez de deixar a pessoa
 * achando que o negócio travou.
 *
 * Nada aqui é decidido no cliente. O socket avisa que houve mexida e a tela
 * relê pela API, como manda docs/WEBSOCKET.md 3.
 */

const POR_PAGINA = 20;

type Aba = "recebidas" | "enviadas" | "leiloes" | "historico";

/** Situações fechadas, que a aba de histórico filtra uma de cada vez. */
const SITUACOES_DO_HISTORICO: readonly LeagueOfferStatus[] = [
  "accepted",
  "rejected",
  "countered",
  "expired",
  "vetoed",
];

/** O que as listas precisam saber da liga para desenhar uma proposta. */
interface ContextoDoMercado {
  leagueId: string;
  meuClubeId: string;
  rodada: number;
  souComissario: boolean;
  aprovacaoExigida: boolean;
  clubes: Map<string, ClubeDaVisao>;
}

export function MercadoOnline({
  leagueId,
  aoVoltar,
}: {
  leagueId: string;
  aoVoltar?: () => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();

  const [liga, definirLiga] = useState<DetalheDeLiga | null>(null);
  const [visao, definirVisao] = useState<VisaoDaLiga | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  const [aba, definirAba] = useState<Aba>("recebidas");
  /**
   * Contador que força as listas a reler.
   *
   * As listas paginam sozinhas e guardam o próprio cursor, então elas não podem
   * depender de um objeto que muda de identidade a cada desenho. Um número que
   * só cresce é o sinal mais barato de "esqueça o que você tem e busque de
   * novo".
   */
  const [versao, definirVersao] = useState(0);
  /**
   * O que acabou de acontecer, dito fora da lista.
   *
   * Toda ação some com a linha que a disparou: aceitar tira a proposta das
   * abertas, contrapor a manda para o outro lado, vetar a encerra. Se o recado
   * morasse na linha, ele sairia da tela junto com ela, e a pessoa veria a
   * proposta sumir sem explicação nenhuma, que é justamente quando ela precisa
   * de explicação.
   */
  const [recado, definirRecado] = useState<string | null>(null);

  const anunciar = useCallback((texto: string | null) => {
    definirRecado(texto);
    definirVersao((atual) => atual + 1);
  }, []);

  const canal = useMemo(() => channelName("league", leagueId), [leagueId]);

  const carregar = useCallback(async () => {
    try {
      const [novaLiga, novaVisao] = await Promise.all([
        sessao.cliente.pedir(
          { metodo: "GET", caminho: `/v1/leagues/${leagueId}` },
          DetalheDeLigaSchema,
        ),
        sessao.cliente.pedir(
          { metodo: "GET", caminho: `/v1/leagues/${leagueId}/view` },
          VisaoDaLigaSchema,
        ),
      ]);
      definirLiga(novaLiga);
      definirVisao(novaVisao);
      definirErro(null);
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    }
  }, [sessao, leagueId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /*
   * O mercado avisa pela central de notificações, no canal da pessoa, e não no
   * canal da liga: uma proposta recebida é assunto de quem recebeu, e o resto
   * da liga não precisa saber que ela existe. O canal da liga continua sendo
   * ouvido porque a virada de rodada mexe no prazo de tudo que está aberto.
   */
  useEffect(() => {
    sessao.socket.assinar(canal);
    const parar = sessao.socket.ouvir((tipo, carga, deQualCanal) => {
      if (tipo === "user.notification") {
        const lido = NotificationPayloadSchema.safeParse(carga);
        if (!lido.success) return;
        if (!lido.data.notification.kind.startsWith("market.")) return;
        definirVersao((atual) => atual + 1);
        return;
      }
      if (deQualCanal !== canal) return;
      if (tipo === "league.round.processed" || tipo === "league.state") {
        definirVersao((atual) => atual + 1);
        void carregar();
      }
    });
    return () => {
      parar();
      sessao.socket.desassinar(canal);
    };
  }, [sessao, canal, carregar]);

  useEffect(() => {
    if (sessao.recarga?.canal === canal) definirVersao((atual) => atual + 1);
  }, [sessao.recarga, canal]);

  if (!liga || !visao) {
    return (
      <Pagina titulo={t("mercadoOnline.titulo")} aoVoltar={aoVoltar}>
        <p className="muted" style={{ textAlign: "center" }}>
          {erro ?? t("online.carregando")}
        </p>
      </Pagina>
    );
  }

  const meuClube = visao.myClub;
  if (!meuClube) {
    return (
      <Pagina titulo={liga.name} aoVoltar={aoVoltar}>
        <p className="muted" style={{ textAlign: "center" }}>
          {t("mercadoOnline.semClube")}
        </p>
      </Pagina>
    );
  }

  if (!liga.settings.market.humanTrades) {
    return (
      <Pagina titulo={liga.name} aoVoltar={aoVoltar}>
        <p className="muted" style={{ textAlign: "center" }}>
          {t("mercadoOnline.mercadoDesligado")}
        </p>
      </Pagina>
    );
  }

  const contexto: ContextoDoMercado = {
    leagueId,
    meuClubeId: meuClube.id,
    rodada: visao.round,
    souComissario: liga.ownerId === sessao.conta?.usuario.id,
    aprovacaoExigida: liga.settings.market.commissionerApproval,
    clubes: new Map(visao.clubs.map((clube) => [clube.id, clube])),
  };

  const abas: Array<{ chave: Aba; rotulo: Chave }> = [
    { chave: "recebidas", rotulo: "mercadoOnline.abas.recebidas" },
    { chave: "enviadas", rotulo: "mercadoOnline.abas.enviadas" },
    { chave: "leiloes", rotulo: "mercadoOnline.abas.leiloes" },
    { chave: "historico", rotulo: "mercadoOnline.abas.historico" },
  ];

  return (
    <Pagina titulo={liga.name} aoVoltar={aoVoltar}>
      {recado && (
        <p role="status" style={{ margin: 0, color: "var(--primary)", fontSize: 12.5 }}>
          {recado}
        </p>
      )}

      <Panel>
        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12 }}
        >
          <Stat label={t("mercadoOnline.rodada")}>
            <span className="num">{visao.round}</span>
          </Stat>
          <Stat label={t("mercadoOnline.situacaoDaLiga")}>
            {t(`ligas.estados.${liga.state}`)}
          </Stat>
          <Stat label={t("hubOnline.caixa")}>
            <Money value={meuClube.cash} />
          </Stat>
          <Stat label={t("mercadoOnline.avalDoComissario")}>
            {contexto.aprovacaoExigida
              ? t("mercadoOnline.avalExigido")
              : t("mercadoOnline.avalDispensado")}
          </Stat>
        </div>
        {contexto.aprovacaoExigida && (
          <p className="muted" style={{ margin: "10px 0 0", fontSize: 11.5 }}>
            {t("mercadoOnline.aprovacaoLigada")}
          </p>
        )}
      </Panel>

      <div className="row" style={{ gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
        {abas.map((entrada) => (
          <button
            key={entrada.chave}
            className="ghost"
            onClick={() => definirAba(entrada.chave)}
            style={
              aba === entrada.chave
                ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                : undefined
            }
          >
            {t(entrada.rotulo)}
          </button>
        ))}
      </div>

      {aba === "recebidas" && (
        <Propostas
          titulo={t("mercadoOnline.abas.recebidas")}
          contexto={contexto}
          direcao={["in"]}
          versao={versao}
          aoMexer={anunciar}
        />
      )}

      {aba === "enviadas" && (
        <>
          <NovaProposta
            contexto={contexto}
            visao={visao}
            razaoMinima={liga.settings.market.minOfferRatio}
            aoEnviar={anunciar}
          />
          <Propostas
            titulo={t("mercadoOnline.abas.enviadas")}
            contexto={contexto}
            direcao={["out"]}
            versao={versao}
            aoMexer={anunciar}
          />
        </>
      )}

      {aba === "leiloes" && (
        <Leiloes contexto={contexto} versao={versao} aoMexer={anunciar} />
      )}

      {aba === "historico" && (
        <Propostas
          titulo={t("mercadoOnline.abas.historico")}
          contexto={contexto}
          direcao={["in", "out"]}
          historico
          versao={versao}
          aoMexer={anunciar}
        />
      )}
    </Pagina>
  );
}

// ---------------------------------------------------------------------------
// Propostas
// ---------------------------------------------------------------------------

/**
 * Lista de propostas com filtro de situação.
 *
 * O filtro é do servidor e não daqui de propósito. A rota só sabe filtrar uma
 * situação por vez, e peneirar a página depois de recebê-la faria o cursor
 * andar sobre linhas que a tela jogou fora: a pessoa clicaria em "carregar
 * mais" e veria uma página vazia sem entender por quê.
 */
function Propostas({
  titulo,
  contexto,
  direcao,
  historico = false,
  versao,
  aoMexer,
}: {
  titulo: string;
  contexto: ContextoDoMercado;
  direcao: OfferDirection[];
  historico?: boolean;
  versao: number;
  /** Conta ao topo da tela o que aconteceu, e pede a releitura da lista. */
  aoMexer: (recado: string | null) => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [situacao, definirSituacao] = useState<LeagueOfferStatus | undefined>(
    historico ? undefined : "open",
  );
  const [itens, definirItens] = useState<PropostaDaLiga[] | null>(null);
  const [cursor, definirCursor] = useState<string | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  const [ocupado, definirOcupado] = useState(false);

  /*
   * `direcao` chega como arranjo literal de quem chama, então ele nasce de novo
   * a cada desenho e não serve de dependência: a busca recomeçaria sem parar. O
   * texto que vai na consulta é o valor de verdade do filtro, e é ele que a
   * releitura observa.
   */
  const filtroDeDirecao = direcao.join(",");

  const filtros: Array<{ rotulo: Chave; valor: LeagueOfferStatus | undefined }> = historico
    ? [
        { rotulo: "mercadoOnline.filtros.todas", valor: undefined },
        ...SITUACOES_DO_HISTORICO.map((qual) => ({
          rotulo: `mercadoOnline.situacoes.${qual}` as Chave,
          valor: qual as LeagueOfferStatus | undefined,
        })),
      ]
    : [
        { rotulo: "mercadoOnline.filtros.abertas", valor: "open" },
        { rotulo: "mercadoOnline.filtros.aguardando", valor: "accepted" },
        { rotulo: "mercadoOnline.filtros.todas", valor: undefined },
      ];

  const carregar = useCallback(
    async (apartirDe: string | null) => {
      definirOcupado(true);
      definirErro(null);
      try {
        const busca = new URLSearchParams({
          limit: String(POR_PAGINA),
          direction: filtroDeDirecao,
        });
        if (situacao) busca.set("status", situacao);
        if (apartirDe) busca.set("cursor", apartirDe);
        const pagina = await sessao.cliente.pedir(
          {
            metodo: "GET",
            caminho: `/v1/leagues/${contexto.leagueId}/offers?${busca.toString()}`,
          },
          PaginaDePropostasSchema,
        );
        definirItens((atuais) => (apartirDe ? [...(atuais ?? []), ...pagina.items] : pagina.items));
        definirCursor(pagina.nextCursor);
      } catch (causa) {
        definirErro(mensagemDoErro(causa));
        definirItens((atuais) => atuais ?? []);
      } finally {
        definirOcupado(false);
      }
    },
    [sessao, contexto.leagueId, filtroDeDirecao, situacao],
  );

  useEffect(() => {
    void carregar(null);
  }, [carregar, versao]);

  return (
    <Panel
      title={titulo}
      action={
        <button
          className="ghost"
          disabled={ocupado}
          onClick={() => void carregar(null)}
          style={{ minHeight: 26, padding: "2px 8px", fontSize: 11 }}
        >
          {t("mercadoOnline.atualizar")}
        </button>
      }
    >
      <div className="row" style={{ gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {filtros.map((filtro) => (
          <button
            key={filtro.rotulo}
            className="ghost"
            onClick={() => definirSituacao(filtro.valor)}
            style={{
              minHeight: 28,
              padding: "2px 10px",
              fontSize: 11.5,
              ...(situacao === filtro.valor
                ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                : {}),
            }}
          >
            {t(filtro.rotulo)}
          </button>
        ))}
      </div>

      {itens === null && (
        <p className="muted" style={{ margin: 0 }}>
          {t("online.carregando")}
        </p>
      )}
      {itens !== null && itens.length === 0 && (
        <p className="muted" style={{ margin: 0 }}>
          {t("mercadoOnline.semPropostas")}
        </p>
      )}

      <div className="grid" style={{ gap: 10 }}>
        {(itens ?? []).map((proposta) => (
          <LinhaDeProposta
            key={proposta.id}
            proposta={proposta}
            contexto={contexto}
            aoMexer={aoMexer}
          />
        ))}
      </div>

      {cursor && (
        <button
          className="ghost"
          disabled={ocupado}
          onClick={() => void carregar(cursor)}
          style={{ marginTop: 10 }}
        >
          {t("mercadoOnline.carregarMais")}
        </button>
      )}
    </Panel>
  );
}

function LinhaDeProposta({
  proposta,
  contexto,
  aoMexer,
}: {
  proposta: PropostaDaLiga;
  contexto: ContextoDoMercado;
  aoMexer: (recado: string | null) => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [formulario, definirFormulario] = useState<"contrapor" | "vetar" | null>(null);
  const [pedido, definirPedido] = useState(String(Math.round(proposta.amount)));
  const [motivo, definirMotivo] = useState("");
  const [ocupado, definirOcupado] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);

  const executar = async (acao: () => Promise<string | null>): Promise<void> => {
    definirErro(null);
    definirOcupado(true);
    try {
      const recado = await acao();
      definirFormulario(null);
      aoMexer(recado);
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(false);
    }
  };

  const responder = (acao: "accept" | "reject" | "counter", valor?: number) =>
    void executar(async () => {
      const resposta = await sessao.cliente.pedir(
        {
          metodo: "POST",
          caminho: `/v1/leagues/${contexto.leagueId}/offers/${proposta.id}/respond`,
          corpo: valor === undefined ? { action: acao } : { action: acao, amount: valor },
        },
        RespostaDePropostaSchema,
      );

      if (acao === "reject") return t("mercadoOnline.recusadaOk");
      if (acao === "counter") {
        return t("mercadoOnline.contrapostaEnviada", {
          situacao: t("mercadoOnline.situacoes.countered"),
        });
      }
      /* Aceitar tem dois desfechos, e a diferença entre eles é o que a pessoa
         precisa saber: entrou na rodada ou está esperando o comissário. */
      if (resposta.queued) {
        return t("mercadoOnline.aceitaNaFila", { rodada: resposta.round ?? contexto.rodada });
      }
      return t("mercadoOnline.aceitaEsperando");
    });

  const vencida = proposta.closesAtRound <= contexto.rodada;
  const aberta = proposta.status === "open" && !vencida;
  const souOAlvo = proposta.toClubId === contexto.meuClubeId;
  const podeResponder = aberta && souOAlvo;
  const podeLiberar =
    contexto.souComissario && proposta.status === "accepted" && !proposta.queued && !vencida;
  const podeVetar =
    contexto.souComissario && (proposta.status === "open" || proposta.status === "accepted");

  const de = contexto.clubes.get(proposta.fromClubId);
  const para = proposta.toClubId === null ? undefined : contexto.clubes.get(proposta.toClubId);

  return (
    <div
      style={{
        padding: "10px 12px",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-sm)",
        background: "var(--surface-2)",
        display: "grid",
        gap: 8,
      }}
    >
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 700 }}>{proposta.playerName}</span>
        <span className="label">{t(`mercadoOnline.situacoes.${proposta.status}`)}</span>
        <span className="label-muted">{t(`mercadoOnline.direcoes.${proposta.direction}`)}</span>
        {proposta.queued && <span className="label">{t("mercadoOnline.naFila")}</span>}
        {proposta.awaitingApproval && (
          <span className="label" style={{ color: "var(--warning)" }}>
            {t("mercadoOnline.esperandoComissario")}
          </span>
        )}
      </div>

      <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
        <Clube clube={de} />
        <span className="muted" aria-hidden="true">
          &rarr;
        </span>
        <Clube clube={para} />
      </div>

      <div
        className="grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10 }}
      >
        <Stat label={t("mercadoOnline.linhaValor")}>
          <Money value={proposta.amount} strong />
        </Stat>
        <Stat label={t("mercadoOnline.linhaParcelas")}>
          {t("mercadoOnline.novaParcela", { total: proposta.installments })}
        </Stat>
      </div>

      <div className="muted" style={{ fontSize: 11.5 }}>
        {vencida
          ? t("mercadoOnline.linhaPrazoVencido", { rodada: proposta.closesAtRound })
          : t("mercadoOnline.linhaPrazo", { rodada: proposta.closesAtRound })}
      </div>

      {proposta.counterAmount !== null && (
        <div className="muted" style={{ fontSize: 11.5 }}>
          {t("mercadoOnline.linhaContraproposta", {
            valor: numero(Math.round(proposta.counterAmount)),
          })}
        </div>
      )}
      {proposta.exchangePlayerId && (
        <div className="muted" style={{ fontSize: 11.5 }}>
          {t("mercadoOnline.linhaTroca", { jogador: proposta.exchangePlayerId })}
        </div>
      )}
      {proposta.loan && (
        <div className="muted" style={{ fontSize: 11.5 }}>
          {t("mercadoOnline.linhaEmprestimo", {
            temporada: proposta.loan.untilSeason,
            percentual: Math.round(proposta.loan.salaryShare * 100),
          })}
        </div>
      )}


      {(podeResponder || podeLiberar || podeVetar) && (
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          {podeResponder && (
            <>
              <button className="primary" disabled={ocupado} onClick={() => responder("accept")}>
                {ocupado ? t("mercadoOnline.enviando") : t("mercadoOnline.aceitar")}
              </button>
              <button className="ghost" disabled={ocupado} onClick={() => responder("reject")}>
                {t("mercadoOnline.recusar")}
              </button>
              <button
                className="ghost"
                disabled={ocupado}
                onClick={() => definirFormulario(formulario === "contrapor" ? null : "contrapor")}
              >
                {t("mercadoOnline.contrapor")}
              </button>
            </>
          )}
          {podeLiberar && (
            <button
              className="primary"
              disabled={ocupado}
              onClick={() =>
                void executar(async () => {
                  const feito = await sessao.cliente.pedir(
                    {
                      metodo: "POST",
                      caminho: `/v1/leagues/${contexto.leagueId}/offers/${proposta.id}/approve`,
                      // A rota não pede corpo, mas o cliente da API anuncia JSON
                      // sempre, e anunciar JSON sem mandar nada faz este
                      // servidor responder 500. O `{}` é o que evita isso.
                      corpo: {},
                    },
                    LiberacaoDePropostaSchema,
                  );
                  return t("mercadoOnline.aprovada", { rodada: feito.round });
                })
              }
            >
              {t("mercadoOnline.aprovar")}
            </button>
          )}
          {podeVetar && (
            <button
              className="ghost"
              disabled={ocupado}
              style={{ color: "var(--danger)" }}
              onClick={() => definirFormulario(formulario === "vetar" ? null : "vetar")}
            >
              {t("mercadoOnline.vetar")}
            </button>
          )}
        </div>
      )}

      {formulario === "contrapor" && (
        <div
          style={{
            display: "grid",
            gap: 8,
            padding: 10,
            borderRadius: "var(--radius-sm)",
            background: "var(--surface-3)",
          }}
        >
          <span className="label">{t("mercadoOnline.contraporTitulo")}</span>
          <p className="muted" style={{ margin: 0, fontSize: 11.5 }}>
            {t("mercadoOnline.contraporExplicacao")}
          </p>
          <label style={{ display: "grid", gap: 4 }}>
            <span className="label-muted">{t("mercadoOnline.contraporValor")}</span>
            <input
              type="number"
              min={1}
              step={1000}
              className="num"
              value={pedido}
              onChange={(evento) => definirPedido(evento.target.value)}
            />
          </label>
          <button
            className="primary"
            disabled={ocupado || Number(pedido) <= 0}
            onClick={() => responder("counter", Math.round(Number(pedido)))}
          >
            {ocupado ? t("mercadoOnline.enviando") : t("mercadoOnline.contraporEnviar")}
          </button>
        </div>
      )}

      {formulario === "vetar" && (
        <div
          style={{
            display: "grid",
            gap: 8,
            padding: 10,
            borderRadius: "var(--radius-sm)",
            background: "var(--surface-3)",
          }}
        >
          <label style={{ display: "grid", gap: 4 }}>
            <span className="label-muted">{t("mercadoOnline.vetoMotivo")}</span>
            <input
              value={motivo}
              maxLength={200}
              onChange={(evento) => definirMotivo(evento.target.value)}
            />
          </label>
          <button
            className="ghost"
            disabled={ocupado}
            style={{ color: "var(--danger)" }}
            onClick={() =>
              void executar(async () => {
                await sessao.cliente.pedir(
                  {
                    metodo: "POST",
                    caminho: `/v1/leagues/${contexto.leagueId}/offers/${proposta.id}/veto`,
                    corpo: motivo.trim() ? { reason: motivo.trim() } : {},
                  },
                  PropostaDaLigaSchema,
                );
                return t("mercadoOnline.vetada");
              })
            }
          >
            {ocupado ? t("mercadoOnline.enviando") : t("mercadoOnline.vetoEnviar")}
          </button>
        </div>
      )}
    </div>
  );
}

/** Escudo e nome de um clube da liga. O identificador cru é o plano de queda. */
function Clube({ clube }: { clube: ClubeDaVisao | undefined }): ReactNode {
  const t = useT();
  if (!clube) {
    return <span className="muted">{t("comum.vazio")}</span>;
  }
  return (
    <span className="row" style={{ gap: 6 }}>
      <Crest colors={clube.colors} short={clube.short} size={22} />
      <span style={{ fontSize: 12.5 }}>{clube.name}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Nova proposta
// ---------------------------------------------------------------------------

/**
 * Proposta por um jogador de outro treinador.
 *
 * A lista de alvos sai da visão filtrada da liga, que é a única fonte de nome de
 * jogador que o cliente tem. Clube de inteligência artificial fica de fora: este
 * mercado é o de humanos, e o servidor recusa o resto com `PLAYER_UNAVAILABLE`,
 * o que seria descobrir tarde demais.
 */
function NovaProposta({
  contexto,
  visao,
  razaoMinima,
  aoEnviar,
}: {
  contexto: ContextoDoMercado;
  visao: VisaoDaLiga;
  razaoMinima: number;
  aoEnviar: (recado: string | null) => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [busca, definirBusca] = useState("");
  const [escolhido, definirEscolhido] = useState<JogadorDeOutroClube | null>(null);
  const [valor, definirValor] = useState("");
  const [parcelas, definirParcelas] = useState(1);
  const [ocupado, definirOcupado] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);

  /*
   * Todo jogador de clube humano entra na lista, e não só os da lista de
   * transferências. `transferable` diz que o dono pendurou a placa de vende-se,
   * e o servidor não exige isso para aceitar uma proposta: filtrar por ele
   * esconderia justamente o craque que a pessoa quer tentar. Quem está na lista
   * ganha uma marca, porque ali a conversa começa mais fácil.
   */
  const negociaveis = useMemo(() => {
    return visao.players
      .filter((jogador) => {
        if (jogador.clubId === null || jogador.clubId === contexto.meuClubeId) return false;
        return contexto.clubes.get(jogador.clubId)?.managedByUser === true;
      })
      .sort((um, outro) => {
        if (um.transferable !== outro.transferable) return um.transferable ? -1 : 1;
        return outro.strength - um.strength;
      });
  }, [visao.players, contexto.clubes, contexto.meuClubeId]);

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const base = termo
      ? negociaveis.filter((jogador) => jogador.name.toLowerCase().includes(termo))
      : negociaveis;
    return base.slice(0, 30);
  }, [negociaveis, busca]);

  const enviar = async (): Promise<void> => {
    if (!escolhido || escolhido.clubId === null) return;
    definirErro(null);
    definirOcupado(true);
    try {
      const criada = await sessao.cliente.pedir(
        {
          metodo: "POST",
          caminho: `/v1/leagues/${contexto.leagueId}/offers`,
          corpo: {
            playerId: escolhido.id,
            toClubId: escolhido.clubId,
            amount: Math.round(Number(valor)),
            installments: parcelas,
          },
        },
        PropostaCriadaSchema,
      );
      const clube = contexto.clubes.get(escolhido.clubId);
      const linhas = [t("mercadoOnline.novaEnviada", { clube: clube?.name ?? escolhido.clubId })];
      if (criada.auctionId) linhas.push(t("mercadoOnline.novaVirouLeilao"));
      definirEscolhido(null);
      definirValor("");
      aoEnviar(linhas.join(" "));
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(false);
    }
  };

  return (
    <Panel title={t("mercadoOnline.novaTitulo")}>
      <p className="muted" style={{ margin: "0 0 10px", fontSize: 11.5 }}>
        {t("mercadoOnline.novaPiso", { percentual: Math.round(razaoMinima * 100) })}
      </p>

      <label style={{ display: "grid", gap: 4 }}>
        <span className="label-muted">{t("mercadoOnline.novaJogador")}</span>
        <input
          value={busca}
          onChange={(evento) => definirBusca(evento.target.value)}
          placeholder={t("mercadoOnline.novaBusca")}
        />
        <span className="muted" style={{ fontSize: 11 }}>
          {t("mercadoOnline.novaEscolha")}
        </span>
      </label>

      {negociaveis.length === 0 && (
        <p className="muted" style={{ margin: "10px 0 0", fontSize: 12 }}>
          {t("mercadoOnline.novaSemJogadores")}
        </p>
      )}

      <div className="grid scroll" style={{ gap: 6, marginTop: 10, maxHeight: 220 }}>
        {filtrados.map((jogador) => {
          const clube = jogador.clubId === null ? undefined : contexto.clubes.get(jogador.clubId);
          const marcado = escolhido?.id === jogador.id;
          return (
            <button
              key={jogador.id}
              className="ghost"
              onClick={() => {
                definirEscolhido(jogador);
                // O valor do jogador é o ponto de partida honesto: o piso da liga
                // é uma fração dele, então abrir o campo vazio só faria a pessoa
                // adivinhar e levar um `OFFER_BELOW_MINIMUM`.
                definirValor(String(Math.round(jogador.value)));
              }}
              style={{
                justifyContent: "flex-start",
                gap: 8,
                minHeight: 34,
                padding: "4px 10px",
                ...(marcado
                  ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                  : {}),
              }}
            >
              <PositionChip pos={jogador.pos} />
              <span style={{ flex: 1, textAlign: "left", fontSize: 12.5 }}>{jogador.name}</span>
              {jogador.transferable && (
                <span className="label" style={{ color: "var(--success)" }}>
                  {t("mercadoOnline.naLista")}
                </span>
              )}
              <StrengthBadge value={jogador.strength} />
              <span className="muted" style={{ fontSize: 11 }}>
                {clube?.short ?? ""}
              </span>
            </button>
          );
        })}
      </div>

      {escolhido && (
        <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
          <div
            className="grid"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}
          >
            <Stat label={t("mercadoOnline.novaJogador")}>{escolhido.name}</Stat>
            <Stat label={t("mercadoOnline.linhaValor")}>
              <Money value={escolhido.value} />
            </Stat>
          </div>

          <label style={{ display: "grid", gap: 4 }}>
            <span className="label-muted">{t("mercadoOnline.novaValor")}</span>
            <input
              type="number"
              min={1}
              step={1000}
              className="num"
              value={valor}
              onChange={(evento) => definirValor(evento.target.value)}
            />
          </label>

          <label style={{ display: "grid", gap: 4 }}>
            <span className="label-muted">{t("mercadoOnline.novaParcelas")}</span>
            <select
              value={parcelas}
              onChange={(evento) => definirParcelas(Number(evento.target.value))}
            >
              {[1, 2, 3].map((quantas) => (
                <option key={quantas} value={quantas}>
                  {t("mercadoOnline.novaParcela", { total: quantas })}
                </option>
              ))}
            </select>
          </label>

          <button
            className="primary"
            disabled={ocupado || Number(valor) <= 0}
            onClick={() => void enviar()}
          >
            {ocupado ? t("mercadoOnline.enviando") : t("mercadoOnline.novaEnviar")}
          </button>
        </div>
      )}

    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Leilões
// ---------------------------------------------------------------------------

function Leiloes({
  contexto,
  versao,
  aoMexer,
}: {
  contexto: ContextoDoMercado;
  versao: number;
  aoMexer: (recado: string | null) => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [situacao, definirSituacao] = useState<"open" | "closed" | undefined>("open");
  const [itens, definirItens] = useState<LeilaoDaLiga[] | null>(null);
  const [cursor, definirCursor] = useState<string | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  const [ocupado, definirOcupado] = useState(false);

  const filtros: Array<{ rotulo: Chave; valor: "open" | "closed" | undefined }> = [
    { rotulo: "mercadoOnline.filtros.leiloesAbertos", valor: "open" },
    { rotulo: "mercadoOnline.filtros.leiloesFechados", valor: "closed" },
    { rotulo: "mercadoOnline.filtros.leiloesTodos", valor: undefined },
  ];

  const carregar = useCallback(
    async (apartirDe: string | null) => {
      definirOcupado(true);
      definirErro(null);
      try {
        const busca = new URLSearchParams({ limit: String(POR_PAGINA) });
        if (situacao) busca.set("status", situacao);
        if (apartirDe) busca.set("cursor", apartirDe);
        const pagina = await sessao.cliente.pedir(
          {
            metodo: "GET",
            caminho: `/v1/leagues/${contexto.leagueId}/auctions?${busca.toString()}`,
          },
          PaginaDeLeiloesSchema,
        );
        definirItens((atuais) => (apartirDe ? [...(atuais ?? []), ...pagina.items] : pagina.items));
        definirCursor(pagina.nextCursor);
      } catch (causa) {
        definirErro(mensagemDoErro(causa));
        definirItens((atuais) => atuais ?? []);
      } finally {
        definirOcupado(false);
      }
    },
    [sessao, contexto.leagueId, situacao],
  );

  useEffect(() => {
    void carregar(null);
  }, [carregar, versao]);

  return (
    <Panel title={t("mercadoOnline.abas.leiloes")}>
      <div className="row" style={{ gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {filtros.map((filtro) => (
          <button
            key={filtro.rotulo}
            className="ghost"
            onClick={() => definirSituacao(filtro.valor)}
            style={{
              minHeight: 28,
              padding: "2px 10px",
              fontSize: 11.5,
              ...(situacao === filtro.valor
                ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                : {}),
            }}
          >
            {t(filtro.rotulo)}
          </button>
        ))}
      </div>

      <p className="muted" style={{ margin: "0 0 10px", fontSize: 11.5 }}>
        {t("mercadoOnline.leilaoFechadoAjuda")}
      </p>

      {itens === null && (
        <p className="muted" style={{ margin: 0 }}>
          {t("online.carregando")}
        </p>
      )}
      {itens !== null && itens.length === 0 && (
        <p className="muted" style={{ margin: 0 }}>
          {t("mercadoOnline.semLeiloes")}
        </p>
      )}

      <div className="grid" style={{ gap: 10 }}>
        {(itens ?? []).map((leilao) => (
          <LinhaDeLeilao key={leilao.id} leilao={leilao} contexto={contexto} aoMexer={aoMexer} />
        ))}
      </div>

      {cursor && (
        <button
          className="ghost"
          disabled={ocupado}
          onClick={() => void carregar(cursor)}
          style={{ marginTop: 10 }}
        >
          {t("mercadoOnline.carregarMais")}
        </button>
      )}
    </Panel>
  );
}

function LinhaDeLeilao({
  leilao,
  contexto,
  aoMexer,
}: {
  leilao: LeilaoDaLiga;
  contexto: ContextoDoMercado;
  aoMexer: (recado: string | null) => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [lance, definirLance] = useState(String(Math.round(leilao.myBid ?? leilao.minBid)));
  const [ocupado, definirOcupado] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);

  const vencido = leilao.closesAtRound <= contexto.rodada;
  const meu = leilao.fromClubId === contexto.meuClubeId;
  const podeDarLance = leilao.status === "open" && !vencido && !meu;
  const dono = leilao.fromClubId === null ? undefined : contexto.clubes.get(leilao.fromClubId);

  const darLance = async (): Promise<void> => {
    definirErro(null);
    definirOcupado(true);
    try {
      const feito = await sessao.cliente.pedir(
        {
          metodo: "POST",
          caminho: `/v1/leagues/${contexto.leagueId}/auctions/${leilao.id}/bid`,
          corpo: { amount: Math.round(Number(lance)) },
        },
        LanceSchema,
      );
      aoMexer(
        t("mercadoOnline.leilaoLanceFeito", {
          valor: numero(feito.amount),
          rodada: feito.closesAtRound,
        }),
      );
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(false);
    }
  };

  return (
    <div
      style={{
        padding: "10px 12px",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-sm)",
        background: "var(--surface-2)",
        display: "grid",
        gap: 8,
      }}
    >
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 700 }}>{leilao.playerName}</span>
        <span className="label">
          {leilao.status === "open"
            ? t("mercadoOnline.filtros.leiloesAbertos")
            : t("mercadoOnline.filtros.leiloesFechados")}
        </span>
        <Clube clube={dono} />
      </div>

      <div
        className="grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10 }}
      >
        <Stat label={t("mercadoOnline.leilaoLanceMinimo")}>
          <Money value={leilao.minBid} />
        </Stat>
        <Stat label={t("mercadoOnline.leilaoMeuLance")}>
          {leilao.myBid === null ? (
            <span className="muted" style={{ fontSize: 12 }}>
              {t("mercadoOnline.leilaoSemLance")}
            </span>
          ) : (
            <Money value={leilao.myBid} strong />
          )}
        </Stat>
        <Stat label={t("mercadoOnline.leilaoClubes", { total: leilao.bidCount })}>
          {vencido
            ? t("mercadoOnline.linhaPrazoVencido", { rodada: leilao.closesAtRound })
            : t("mercadoOnline.linhaPrazo", { rodada: leilao.closesAtRound })}
        </Stat>
      </div>

      {leilao.status === "closed" && (
        <div className="muted" style={{ fontSize: 11.5 }}>
          {leilao.winnerClubId && leilao.winningAmount !== null
            ? t("mercadoOnline.leilaoFechado", { valor: numero(leilao.winningAmount) })
            : t("mercadoOnline.leilaoSemVencedor")}
        </div>
      )}


      {podeDarLance && (
        <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={{ display: "grid", gap: 4, flex: 1, minWidth: 140 }}>
            <span className="label-muted">{t("mercadoOnline.leilaoLance")}</span>
            <input
              type="number"
              min={Math.round(leilao.minBid)}
              step={1000}
              className="num"
              value={lance}
              onChange={(evento) => definirLance(evento.target.value)}
            />
          </label>
          <button
            className="primary"
            disabled={ocupado || Number(lance) < leilao.minBid}
            onClick={() => void darLance()}
          >
            {ocupado ? t("mercadoOnline.enviando") : t("mercadoOnline.leilaoDarLance")}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Peças de tela
// ---------------------------------------------------------------------------

